export type Fas =
  | "Prospect"
  | "Förinkubation"
  | "Inkubation"
  | "Tillväxt"
  | "Exit"
  | "Alumni"
  | "Pausat";

export type Mognad =
  | "Idé"
  | "MVP"
  | "Pilotkunder"
  | "Produkt i marknad"
  | "Tillväxt"
  | "Skalning"
  | "Internationell";

export type Bransch =
  | "AI"
  | "SaaS"
  | "Deeptech"
  | "Healthtech"
  | "Medtech"
  | "Greentech"
  | "Industri"
  | "Digitalisering"
  | "Edtech"
  | "Fintech"
  | "Mobility"
  | "Energi"
  | "Life Science";

export type FinansieringTyp =
  | "Riskkapital"
  | "Ängelinvestering"
  | "Vinnova"
  | "Tillväxtverket"
  | "EU"
  | "Region"
  | "Almi-lån"
  | "Innovationsbidrag"
  | "Bootstrap";

export interface Finansiering {
  datum: string;
  typ: FinansieringTyp;
  belopp: number; // SEK
  investerare?: string;
}

export interface Milstolpe {
  datum: string;
  rubrik: string;
  beskrivning?: string;
}

export type BolagStatus =
  | "Aktivt"
  | "Inaktivt"
  | "Avvecklat"
  | "Konkurs"
  | "Uppköpt";

export type ProspectKalla =
  | "Hemsida"
  | "Event"
  | "Partner"
  | "Universitet"
  | "Rekommendation"
  | "Inkommande mejl"
  | "Sociala medier"
  | "Annat";

export const KALLOR: ProspectKalla[] = [
  "Hemsida",
  "Event",
  "Partner",
  "Universitet",
  "Rekommendation",
  "Inkommande mejl",
  "Sociala medier",
  "Annat",
];

// Kommuner i Region Örebro län
export type Kommun =
  | "Örebro"
  | "Laxå"
  | "Hallsberg"
  | "Kumla"
  | "Askersund"
  | "Lekeberg"
  | "Nora"
  | "Lindesberg"
  | "Hällefors"
  | "Karlskoga"
  | "Degerfors"
  | "Ljusnarsberg";

export const KOMMUNER: Kommun[] = [
  "Örebro", "Laxå", "Hallsberg", "Kumla", "Askersund", "Lekeberg",
  "Nora", "Lindesberg", "Hällefors", "Karlskoga", "Degerfors", "Ljusnarsberg",
];

// Inflöde — varifrån bolaget kom in i Inkubera (kontaktyta innan förinkubation)
export type Inflode =
  | "Akademi Örebro"
  | "Almi Företagspartner"
  | "Näringslivet"
  | "Region Örebro län"
  | "Science Park"
  | "Kommun / Näringslivskontor"
  | "Egen kontakt"
  | "Annan inkubator";

export const INFLODEN: Inflode[] = [
  "Akademi Örebro",
  "Almi Företagspartner",
  "Näringslivet",
  "Region Örebro län",
  "Science Park",
  "Kommun / Näringslivskontor",
  "Egen kontakt",
  "Annan inkubator",
];

// Readiness-Levels (RL) — utvecklingsmått som följs över tid per bolag
export interface RLPoint {
  datum: string; // ISO
  brl: number; // Business Readiness Level
  mrl: number; // Market Readiness Level
  srl: number; // Sustainability Readiness Level
  trl: number; // Technology Readiness Level
}

export const RL_NIVAER = ["BRL", "MRL", "SRL", "TRL"] as const;
export type RLNivaa = typeof RL_NIVAER[number];

export interface Bolag {
  id: string;
  namn: string;
  beskrivning: string;
  webb: string;
  orgnr: string;
  ort: string;
  kommun?: Kommun;
  inflöde?: Inflode;
  rlHistorik?: RLPoint[];
  fas: Fas;
  status?: BolagStatus;
  källa?: ProspectKalla;
  inkomDatum?: string; // ISO datum då bolaget kom in i pipeline
  mognad: Mognad;
  branscher: Bransch[];
  affarsutvecklare: string;
  kontakt: string;
  startdatum: string;
  logoFarg: string;
  initialer: string;
  finansiering: Finansiering[];
  milstolpar: Milstolpe[];
  hälsoscore: number;
  omsattning: number;
  anstallda: number;
}

export const FASER: Fas[] = [
  "Prospect",
  "Förinkubation",
  "Inkubation",
  "Tillväxt",
  "Exit",
  "Alumni",
  "Pausat",
];

export const STATUSAR: BolagStatus[] = [
  "Aktivt",
  "Inaktivt",
  "Avvecklat",
  "Konkurs",
  "Uppköpt",
];

export const MOGNADSSKALA: Mognad[] = [
  "Idé",
  "MVP",
  "Pilotkunder",
  "Produkt i marknad",
  "Tillväxt",
  "Skalning",
  "Internationell",
];

export const fasFarg: Record<Fas, string> = {
  Prospect: "oklch(0.75 0.04 260)",
  Förinkubation: "oklch(0.7 0.13 230)",
  Inkubation: "oklch(0.78 0.16 130)",
  Tillväxt: "oklch(0.7 0.16 50)",
  Exit: "oklch(0.6 0.2 320)",
  Alumni: "oklch(0.55 0.05 260)",
  Pausat: "oklch(0.65 0.04 30)",
};

const make = (b: Omit<Bolag, "initialer" | "hälsoscore"> & { hälsoscore?: number }): Bolag => ({
  ...b,
  initialer: b.namn
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0])
    .join("")
    .toUpperCase(),
  hälsoscore: b.hälsoscore ?? 70,
});

export const BOLAG: Bolag[] = [
  // ===== Inkubationsbolag =====
  make({
    id: "axintor",
    namn: "Axintor",
    beskrivning:
      "Utvecklar digitala, affärsinriktade ledningssystem som stärker struktur, processer och medarbetarengagemang i organisationer.",
    webb: "axintor.se",
    orgnr: "—",
    ort: "Örebro",
    fas: "Inkubation",
    mognad: "Pilotkunder",
    branscher: ["SaaS", "Digitalisering"],
    affarsutvecklare: "Inkubera",
    kontakt: "—",
    startdatum: "2023-09-01",
    logoFarg: "oklch(0.55 0.13 250)",
    finansiering: [],
    milstolpar: [{ datum: "2024-02-01", rubrik: "Pilotkunder onboardade" }],
    hälsoscore: 76,
    omsattning: 0,
    anstallda: 3,
  }),
  make({
    id: "monetax",
    namn: "Monetax",
    beskrivning:
      "Hjälper företag och privatpersoner att hantera och redovisa kryptovalutor korrekt enligt svensk skattelagstiftning.",
    webb: "monetax.se",
    orgnr: "—",
    ort: "Örebro",
    fas: "Inkubation",
    mognad: "Produkt i marknad",
    branscher: ["Fintech", "SaaS"],
    affarsutvecklare: "Inkubera",
    kontakt: "—",
    startdatum: "2023-04-01",
    logoFarg: "oklch(0.65 0.14 200)",
    finansiering: [],
    milstolpar: [{ datum: "2024-04-15", rubrik: "Lansering av plattform" }],
    hälsoscore: 78,
    omsattning: 0,
    anstallda: 4,
  }),
  make({
    id: "spacebetween",
    namn: "SpaceBetween",
    beskrivning:
      "Har utvecklat en innovativ mugg som hjälper barn med ätstörningar eller medicinintag genom gamification.",
    webb: "spacebetween.se",
    orgnr: "—",
    ort: "Örebro",
    fas: "Inkubation",
    mognad: "Pilotkunder",
    branscher: ["Medtech", "Healthtech"],
    affarsutvecklare: "Inkubera",
    kontakt: "—",
    startdatum: "2023-06-01",
    logoFarg: "oklch(0.65 0.18 20)",
    finansiering: [],
    milstolpar: [{ datum: "2024-06-01", rubrik: "Klinisk pilot inledd" }],
    hälsoscore: 74,
    omsattning: 0,
    anstallda: 3,
  }),
  make({
    id: "tendoer",
    namn: "Tendoer",
    beskrivning:
      "Effektiviserar upphandlingar genom AI och samlar processer i en plattform för både köpare och säljare.",
    webb: "tendoer.se",
    orgnr: "—",
    ort: "Örebro",
    fas: "Inkubation",
    mognad: "Produkt i marknad",
    branscher: ["SaaS", "AI", "Digitalisering"],
    affarsutvecklare: "Inkubera",
    kontakt: "—",
    startdatum: "2023-02-01",
    logoFarg: "oklch(0.6 0.15 320)",
    finansiering: [],
    milstolpar: [{ datum: "2024-09-01", rubrik: "Första betalande kunder" }],
    hälsoscore: 80,
    omsattning: 0,
    anstallda: 5,
  }),
  make({
    id: "zetopack",
    namn: "ZetoPack",
    beskrivning:
      "Utvecklar cirkulära, plastfria och temperaturtåliga förpackningar baserade på växtmaterial.",
    webb: "zetopack.se",
    orgnr: "—",
    ort: "Örebro",
    fas: "Inkubation",
    mognad: "MVP",
    branscher: ["Greentech", "Industri"],
    affarsutvecklare: "Inkubera",
    kontakt: "—",
    startdatum: "2023-10-01",
    logoFarg: "oklch(0.78 0.16 130)",
    finansiering: [],
    milstolpar: [{ datum: "2024-11-01", rubrik: "Materialtester avslutade" }],
    hälsoscore: 72,
    omsattning: 0,
    anstallda: 3,
  }),
  make({
    id: "nordic-flight-stack",
    namn: "Nordic Flight Stack",
    beskrivning:
      "Utvecklar robusta autonoma navigationssystem för drönare utan beroende av GPS.",
    webb: "nordicflightstack.se",
    orgnr: "—",
    ort: "Örebro",
    fas: "Inkubation",
    mognad: "MVP",
    branscher: ["Deeptech", "AI", "Mobility"],
    affarsutvecklare: "Inkubera",
    kontakt: "—",
    startdatum: "2024-01-15",
    logoFarg: "oklch(0.5 0.1 240)",
    finansiering: [],
    milstolpar: [{ datum: "2024-08-01", rubrik: "Första flygtester genomförda" }],
    hälsoscore: 75,
    omsattning: 0,
    anstallda: 4,
  }),
  make({
    id: "longevience",
    namn: "Longevience",
    beskrivning:
      "Forskningsbaserat bolag med flera patent och vetenskapliga publikationer. Utvecklar innovativa kosttillskott och har lanserat AgeViva, baserat på elektromagnetiskt behandlade jästceller.",
    webb: "longevience.com",
    orgnr: "—",
    ort: "Örebro",
    fas: "Tillväxt",
    mognad: "Produkt i marknad",
    branscher: ["Medtech", "Healthtech", "Life Science", "Deeptech"],
    affarsutvecklare: "Inkubera",
    kontakt: "—",
    startdatum: "2022-01-01",
    logoFarg: "oklch(0.6 0.18 350)",
    finansiering: [],
    milstolpar: [
      { datum: "2023-09-01", rubrik: "Patent godkänt" },
      { datum: "2024-05-01", rubrik: "AgeViva lanserad på marknaden" },
      { datum: "2025-01-01", rubrik: "Förbereder exit" },
    ],
    hälsoscore: 86,
    omsattning: 0,
    anstallda: 6,
  }),
  make({
    id: "steamforce-medical",
    namn: "Steamforce Medical",
    beskrivning:
      "Dissinkfect® utvecklar självdesinficerande disksystem för vårdmiljöer. Avancerad värmeteknik eliminerar bakterier direkt i diskhon och förebygger smittspridning.",
    webb: "steamforce.se",
    orgnr: "—",
    ort: "Örebro",
    fas: "Inkubation",
    mognad: "MVP",
    branscher: ["Medtech", "Healthtech", "Industri"],
    affarsutvecklare: "Inkubera",
    kontakt: "—",
    startdatum: "2023-11-01",
    logoFarg: "oklch(0.7 0.13 200)",
    finansiering: [],
    milstolpar: [{ datum: "2024-10-01", rubrik: "Prototyp i klinisk testmiljö" }],
    hälsoscore: 73,
    omsattning: 0,
    anstallda: 3,
  }),

  // ===== Prospects (dealflow under utvärdering) =====
  make({
    id: "handdesinfektionssystem",
    namn: "Handdesinfektion (Katawazai)",
    beskrivning:
      "Kvalificerad handdesinfektionsapparat som täcker områden vanlig hygien missar (mellan fingrar, naglar) — för sjukhusmiljö och fältbruk. Innovatör: Asmatullah Katawazai, överläkare och affilierad forskare VO kirurgi (Region Örebro län), basgruppshandledare läkarprogrammet ORU. Bolag ej startat.",
    webb: "—",
    orgnr: "—",
    ort: "Örebro",
    fas: "Prospect",
    mognad: "Idé",
    branscher: ["Medtech", "Healthtech"],
    affarsutvecklare: "Inkubera",
    kontakt: "Asmatullah Katawazai",
    startdatum: "2024-09-01",
    inkomDatum: "2024-09-01",
    källa: "Universitet",
    logoFarg: "oklch(0.65 0.16 160)",
    finansiering: [],
    milstolpar: [{ datum: "2024-09-01", rubrik: "Dealflow — forskning + sjukhuset" }],
    hälsoscore: 60,
    omsattning: 0,
    anstallda: 1,
  }),
  make({
    id: "ai-ambulans",
    namn: "AI-stöd ambulans (Höglund)",
    beskrivning:
      "AI/hjälpmedel för mer korrekt vårdnivåbedömning inom akutvård/ambulans samt plattform för specialistutbildning matchat mot kompetens i sjukvården. Innovatör: Erik Höglund, medicine doktor och specialistsjuksköterska ambulanssjukvård (forskning: hänvisning, patientsäkerhet). Bolag ej startat.",
    webb: "—",
    orgnr: "—",
    ort: "Örebro",
    fas: "Prospect",
    mognad: "Idé",
    branscher: ["Healthtech", "AI"],
    affarsutvecklare: "Inkubera",
    kontakt: "Erik Höglund",
    startdatum: "2024-10-01",
    inkomDatum: "2024-10-01",
    källa: "Universitet",
    logoFarg: "oklch(0.65 0.18 20)",
    finansiering: [],
    milstolpar: [{ datum: "2024-10-01", rubrik: "Dealflow — forskning + sjukhuset" }],
    hälsoscore: 62,
    omsattning: 0,
    anstallda: 1,
  }),
  make({
    id: "pfas-rening",
    namn: "PFAS-rening (Alwan)",
    beskrivning:
      "Helt ny metod för rening av vatten, luft och jord från PFAS. Två pilotprojekt startar juni/juli. Innovatör: Riham Alwan, laboratory engineer, MSc Chemistry — environmental forensics (ORU). Bolag ej startat.",
    webb: "—",
    orgnr: "—",
    ort: "Örebro",
    fas: "Prospect",
    mognad: "Idé",
    branscher: ["Greentech", "Deeptech"],
    affarsutvecklare: "Inkubera",
    kontakt: "Riham Alwan",
    startdatum: "2024-11-01",
    inkomDatum: "2024-11-01",
    källa: "Universitet",
    logoFarg: "oklch(0.7 0.13 200)",
    finansiering: [],
    milstolpar: [
      { datum: "2024-11-01", rubrik: "Dealflow — forskning" },
      { datum: "2025-06-01", rubrik: "Pilotprojekt planerade start" },
    ],
    hälsoscore: 64,
    omsattning: 0,
    anstallda: 1,
  }),
  make({
    id: "greeware",
    namn: "Greeware",
    beskrivning:
      "Nytt hållbart gummimaterial baserat på skog + citrus — 8 års forskning. Två medgrundare: en forskare/kemist och en med industribakgrund. Bolag finns (ej verifierat ännu).",
    webb: "—",
    orgnr: "—",
    ort: "Örebro",
    fas: "Prospect",
    mognad: "MVP",
    branscher: ["Greentech", "Industri", "Deeptech"],
    affarsutvecklare: "Inkubera",
    kontakt: "—",
    startdatum: "2024-08-01",
    inkomDatum: "2024-08-01",
    källa: "Partner",
    logoFarg: "oklch(0.78 0.16 130)",
    finansiering: [],
    milstolpar: [{ datum: "2024-12-01", rubrik: "Dealflow — industri + forskning" }],
    hälsoscore: 66,
    omsattning: 0,
    anstallda: 2,
  }),
  make({
    id: "healthish",
    namn: "Healthish",
    beskrivning:
      "AI-app — nytt sätt att matcha kvalificerad personal till sjukvården. Grundare: student på läkarprogrammet ORU (examen i vår). Bolag ej startat.",
    webb: "—",
    orgnr: "—",
    ort: "Örebro",
    fas: "Prospect",
    mognad: "Idé",
    branscher: ["Healthtech", "AI", "SaaS"],
    affarsutvecklare: "Inkubera",
    kontakt: "—",
    startdatum: "2024-09-15",
    inkomDatum: "2024-09-15",
    källa: "Universitet",
    logoFarg: "oklch(0.65 0.16 160)",
    finansiering: [],
    milstolpar: [{ datum: "2024-12-15", rubrik: "Dealflow — sjukvården + student ORU" }],
    hälsoscore: 64,
    omsattning: 0,
    anstallda: 1,
  }),
  make({
    id: "wilsonai",
    namn: "WilsonAI",
    beskrivning:
      "AI-assistent som hjälper personer med NPF-problematik att leva organiserat. Unikt långtidsminne/\"hjärna\" — säljbart B2B. Grundare: två studenter (systemvetare + AI-profil), båda egenföretagare. Ska starta nytt bolag; använder just nu Epileptico AB.",
    webb: "—",
    orgnr: "—",
    ort: "Örebro",
    fas: "Prospect",
    mognad: "MVP",
    branscher: ["AI", "SaaS", "Healthtech"],
    affarsutvecklare: "Inkubera",
    kontakt: "—",
    startdatum: "2024-07-01",
    inkomDatum: "2024-07-01",
    källa: "Universitet",
    logoFarg: "oklch(0.6 0.15 320)",
    finansiering: [],
    milstolpar: [{ datum: "2024-11-15", rubrik: "Dealflow — universitetet" }],
    hälsoscore: 68,
    omsattning: 0,
    anstallda: 2,
  }),
];

// ---- Aggregeringar ----
export const totalKapital = (b: Bolag) => b.finansiering.reduce((s, f) => s + f.belopp, 0);

export const formatSEK = (n: number) => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)} Mkr`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)} tkr`;
  return `${n} kr`;
};

export const offentligaTyper: FinansieringTyp[] = [
  "Vinnova",
  "Tillväxtverket",
  "EU",
  "Region",
  "Innovationsbidrag",
];
export const privataTyper: FinansieringTyp[] = ["Riskkapital", "Ängelinvestering", "Almi-lån", "Bootstrap"];

// ---- Demo-data: källor & prospects (fiktivt) ----
// Tilldelar källor och inkomstdatum till befintliga bolag deterministiskt.
const SEED_KALLOR: ProspectKalla[] = [
  "Universitet", "Event", "Partner", "Hemsida", "Rekommendation",
  "Inkommande mejl", "Universitet", "Event", "Partner", "Hemsida",
  "Rekommendation", "Sociala medier", "Universitet", "Event", "Partner",
];
BOLAG.forEach((b, i) => {
  if (!b.källa) b.källa = SEED_KALLOR[i % SEED_KALLOR.length];
  if (!b.inkomDatum) b.inkomDatum = b.startdatum;
});

// Lägg till några prospects (fiktiva) som inkommit under innevarande år
const innevarandeAr = new Date().getFullYear();
const ddmm = (m: number, d: number) => `${innevarandeAr}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

const PROSPECTS: Bolag[] = ([
  { id: "p-bioloop", namn: "BioLoop", källa: "Universitet" as ProspectKalla, inkomDatum: ddmm(1, 18), branscher: ["Greentech", "Life Science"] as Bransch[] },
  { id: "p-formfit", namn: "FormFit", källa: "Hemsida" as ProspectKalla, inkomDatum: ddmm(2, 4), branscher: ["Healthtech", "SaaS"] as Bransch[] },
  { id: "p-quantix", namn: "Quantix Labs", källa: "Partner" as ProspectKalla, inkomDatum: ddmm(2, 22), branscher: ["Deeptech", "AI"] as Bransch[] },
  { id: "p-edulab", namn: "EduLab", källa: "Event" as ProspectKalla, inkomDatum: ddmm(3, 9), branscher: ["Edtech", "AI"] as Bransch[] },
  { id: "p-nordheat", namn: "NordHeat", källa: "Rekommendation" as ProspectKalla, inkomDatum: ddmm(3, 28), branscher: ["Energi", "Greentech"] as Bransch[] },
  { id: "p-fleetly", namn: "Fleetly", källa: "Inkommande mejl" as ProspectKalla, inkomDatum: ddmm(4, 12), branscher: ["Mobility", "SaaS"] as Bransch[] },
  { id: "p-mediscan", namn: "MediScan", källa: "Universitet" as ProspectKalla, inkomDatum: ddmm(4, 30), branscher: ["Medtech", "AI"] as Bransch[] },
  { id: "p-payloop", namn: "PayLoop", källa: "Sociala medier" as ProspectKalla, inkomDatum: ddmm(5, 14), branscher: ["Fintech", "SaaS"] as Bransch[] },
  { id: "p-soilsense", namn: "SoilSense", källa: "Event" as ProspectKalla, inkomDatum: ddmm(5, 27), branscher: ["Greentech", "Industri"] as Bransch[] },
  { id: "p-vocalia", namn: "Vocalia", källa: "Partner" as ProspectKalla, inkomDatum: ddmm(6, 8), branscher: ["AI", "SaaS"] as Bransch[] },
  { id: "p-recyclo", namn: "Recyclo", källa: "Hemsida" as ProspectKalla, inkomDatum: ddmm(6, 22), branscher: ["Greentech", "Industri"] as Bransch[] },
  { id: "p-skillweave", namn: "SkillWeave", källa: "Universitet" as ProspectKalla, inkomDatum: ddmm(7, 3), branscher: ["Edtech", "SaaS"] as Bransch[] },
]).map((p) =>
  make({
    id: p.id,
    namn: p.namn,
    beskrivning: "Prospektsbolag under utvärdering.",
    webb: "—",
    orgnr: "—",
    ort: "Örebro",
    fas: "Prospect",
    mognad: "Idé",
    branscher: p.branscher,
    affarsutvecklare: "Inkubera",
    kontakt: "—",
    startdatum: p.inkomDatum,
    logoFarg: "oklch(0.7 0.05 260)",
    finansiering: [],
    milstolpar: [],
    omsattning: 0,
    anstallda: 1,
    källa: p.källa,
    inkomDatum: p.inkomDatum,
  }),
);
BOLAG.push(...PROSPECTS);

// Lägg på lite fiktiv finansiering på inkubationsbolag för att rapporten ska bli intressant
const FIN_SEED: Record<string, Finansiering[]> = {
  axintor: [
    { datum: ddmm(2, 15), typ: "Vinnova", belopp: 800_000, investerare: "Vinnova" },
    { datum: ddmm(5, 1), typ: "Ängelinvestering", belopp: 1_500_000, investerare: "Lokal ängelinvesterare" },
  ],
  monetax: [
    { datum: ddmm(3, 10), typ: "Almi-lån", belopp: 500_000, investerare: "Almi" },
  ],
  spacebetween: [
    { datum: ddmm(1, 22), typ: "Innovationsbidrag", belopp: 300_000 },
    { datum: ddmm(6, 4), typ: "EU", belopp: 1_200_000, investerare: "EIC Accelerator" },
  ],
  tendoer: [
    { datum: ddmm(4, 18), typ: "Riskkapital", belopp: 4_500_000, investerare: "Norrsken VC" },
  ],
  zetopack: [
    { datum: ddmm(2, 28), typ: "Tillväxtverket", belopp: 700_000 },
  ],
  "nordic-flight-stack": [
    { datum: ddmm(5, 20), typ: "Vinnova", belopp: 1_000_000 },
  ],
  longevience: [
    { datum: ddmm(3, 1), typ: "Riskkapital", belopp: 8_000_000, investerare: "Industrifonden" },
    { datum: ddmm(6, 15), typ: "Region", belopp: 400_000 },
  ],
  "steamforce-medical": [
    { datum: ddmm(4, 5), typ: "Innovationsbidrag", belopp: 350_000 },
  ],
};
BOLAG.forEach((b) => {
  const seed = FIN_SEED[b.id];
  if (seed && b.finansiering.length === 0) b.finansiering = seed;
});

// Markera fiktiv status (exits / avslutade) för rapporten
const STATUS_SEED: Record<string, BolagStatus> = {
  longevience: "Uppköpt",       // Exit
  zetopack: "Avvecklat",        // Avslutat avtal i förtid
  "nordic-flight-stack": "Aktivt",
};
BOLAG.forEach((b) => {
  if (STATUS_SEED[b.id]) b.status = STATUS_SEED[b.id];
});

// ---- Seed: kommun, inflöde och RL-historik (deterministiskt, fiktivt) ----
const KOMMUN_OVERRIDES: Record<string, Kommun> = {
  axintor: "Örebro",
  monetax: "Kumla",
  spacebetween: "Örebro",
  tendoer: "Örebro",
  zetopack: "Laxå",
  "nordic-flight-stack": "Karlskoga",
  longevience: "Örebro",
  "steamforce-medical": "Lindesberg",
  handdesinfektionssystem: "Hallsberg",
  "ai-ambulans": "Örebro",
  "pfas-rening": "Askersund",
  greeware: "Lekeberg",
  healthish: "Örebro",
  wilsonai: "Nora",
  "p-bioloop": "Örebro",
  "p-formfit": "Kumla",
  "p-quantix": "Örebro",
  "p-edulab": "Hallsberg",
  "p-nordheat": "Hällefors",
  "p-fleetly": "Karlskoga",
  "p-mediscan": "Örebro",
  "p-payloop": "Örebro",
  "p-soilsense": "Degerfors",
  "p-vocalia": "Örebro",
  "p-recyclo": "Laxå",
  "p-skillweave": "Örebro",
};

const INFLODE_BY_KALLA: Record<ProspectKalla, Inflode> = {
  Universitet: "Akademi Örebro",
  Partner: "Almi Företagspartner",
  Event: "Region Örebro län",
  Hemsida: "Egen kontakt",
  Rekommendation: "Näringslivet",
  "Inkommande mejl": "Egen kontakt",
  "Sociala medier": "Egen kontakt",
  Annat: "Annan inkubator",
};

// Generera RL-historik utifrån mognadsnivå — börjar lågt, växer mot målnivå
function genRLHistorik(b: Bolag): RLPoint[] {
  const mIdx = MOGNADSSKALA.indexOf(b.mognad);
  const målTrl = Math.min(9, 2 + mIdx);
  const start = new Date(b.startdatum);
  const slut = new Date();
  const månader = Math.max(
    3,
    Math.min(
      18,
      (slut.getFullYear() - start.getFullYear()) * 12 + (slut.getMonth() - start.getMonth()),
    ),
  );
  const steg = Math.max(2, Math.floor(månader / 4));
  const points: RLPoint[] = [];
  const jitter = (i: number, off: number) => ((b.namn.length + i * 3 + off) % 3) - 1;
  for (let i = 0; i <= 4; i++) {
    const d = new Date(start);
    d.setMonth(d.getMonth() + i * steg);
    if (d > slut) break;
    const progress = i / 4;
    const base = Math.round(2 + (målTrl - 2) * progress);
    points.push({
      datum: d.toISOString().slice(0, 10),
      brl: Math.max(1, Math.min(9, base + jitter(i, 1))),
      mrl: Math.max(1, Math.min(9, base + jitter(i, 2) - 1)),
      srl: Math.max(1, Math.min(9, base + jitter(i, 3))),
      trl: Math.max(1, Math.min(9, base + jitter(i, 4) + 1)),
    });
  }
  return points;
}

BOLAG.forEach((b) => {
  if (!b.kommun) b.kommun = KOMMUN_OVERRIDES[b.id] ?? "Örebro";
  if (!b.inflöde) b.inflöde = b.källa ? INFLODE_BY_KALLA[b.källa] : "Egen kontakt";
  if (!b.rlHistorik || b.rlHistorik.length === 0) b.rlHistorik = genRLHistorik(b);
});

// ---- Aggregeringar för RL-rörelse ----
export type RLDelta = { brl: number; mrl: number; srl: number; trl: number; total: number };

export function rlDelta(b: Bolag): RLDelta | null {
  const h = b.rlHistorik;
  if (!h || h.length < 2) return null;
  const first = h[0];
  const last = h[h.length - 1];
  const d = {
    brl: last.brl - first.brl,
    mrl: last.mrl - first.mrl,
    srl: last.srl - first.srl,
    trl: last.trl - first.trl,
    total: 0,
  };
  d.total = d.brl + d.mrl + d.srl + d.trl;
  return d;
}

export function portfolioRLRorelse(bolag: Bolag[] = BOLAG) {
  const deltas = bolag
    .filter((b) => b.fas !== "Prospect")
    .map((b) => rlDelta(b))
    .filter((d): d is RLDelta => d !== null);
  if (deltas.length === 0) {
    return { antal: 0, snittPerNivaa: 0, snittTotal: 0, andelMedRorelse: 0 };
  }
  const snittTotal = deltas.reduce((s, d) => s + d.total, 0) / deltas.length;
  const medRorelse = deltas.filter((d) => d.total > 0).length;
  return {
    antal: deltas.length,
    snittPerNivaa: +(snittTotal / 4).toFixed(2),
    snittTotal: +snittTotal.toFixed(2),
    andelMedRorelse: Math.round((medRorelse / deltas.length) * 100),
  };
}
